// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The fixed-step driver (M3-02, PH-022, ARCH-012).
//
// The renderer runs at whatever rate the display and the machine allow — 30 Hz on a tired
// phone, 60 on a laptop, 144 on a gaming monitor, 72 or 90 or 120 in a headset. The simulation
// runs at 1000 Hz. Always. On every device, under any load, in every one of those cases.
//
// **This file is the entire reason that is true**, and it is the load-bearing piece of the
// whole architecture: a leaderboard score is validated by re-simulating the player's input
// trace on a server with no display at all, so a simulation that took its timestep from the
// display would produce a different game on every machine and validate on none of them.
//
// Three rules, and each one has a failure mode that looks like something else:
//
//  1. **The renderer never advances the simulation.** It asks for the state *between* two
//     completed steps and interpolates. A renderer that stepped the world would make the
//     physics depend on the frame rate, which shows up as "the flippers feel different on my
//     laptop" and is untraceable from there.
//  2. **Leftover time is carried, never dropped and never rounded.** A 60 Hz frame is 16.67
//     steps; dropping the fraction loses 40 ms of simulation a second, and a game slowly runs
//     slow. The accumulator keeps it. Note what this does *not* promise: a second of wall clock
//     delivered as thirty frames of 1/30 sums to 0.9999999999999999, so it is 999 whole steps
//     and a sliver, and the sliver is carried rather than rounded up. Inventing that last step
//     would be inventing a millisecond a second of divergence from a server replaying the
//     same trace.
//  3. **A frame that cannot afford its steps declares lost time rather than spiralling.**
//     Falling behind means more steps next frame, which takes longer, which means more steps
//     again. `MAX_STEPS_PER_FRAME` breaks that loop; the governor above steps the *render*
//     tier down in response, never the simulation rate (ARCH-012).
//  4. **Sustained lost time stops the game rather than stealing the ball.** This is the second
//     half of PH-023, and it is the half that is easy to leave undone, because the first half —
//     declaring the loss — puts a number in a diagnostic and lets the game carry on. A ball
//     travels up to six metres a second, so a quarter-second of lost simulation is more than a
//     playfield length of ball the player never saw and could not have flipped. The driver
//     detects that and latches it; deciding whether a ball is in play, and telling the player,
//     belong to the host.

import { MAX_STEPS_PER_FRAME, STEP_DT, STEP_HZ } from '@core/sim/constants'

/** What the renderer needs to draw a frame, and nothing it could use to change the game. */
export interface FrameState {
  /** Simulation step index of the most recently completed step. */
  readonly step: number
  /**
   * How far the frame sits past that step, 0…1.
   *
   * The renderer draws `lerp(previous, current, alpha)`. Without it, a 1000 Hz simulation
   * sampled at 60 Hz shows the ball at whichever of sixteen positions the frame happened to
   * land on, and the motion judders in a way that reads as a bad physics engine.
   */
  readonly alpha: number
  /** Steps advanced during this frame. Zero is normal on a fast display. */
  readonly stepsRun: number
  /** Steps the driver refused to run because the frame was already over budget. */
  readonly stepsDropped: number
  /**
   * Sustained lost time has been detected and not yet acknowledged (PH-023).
   *
   * Latched, so a host that reads it once a frame cannot miss the edge. It is a *report*, not
   * an action: the driver keeps running until someone calls `pause()`, because only the host
   * knows whether a ball is in play and whether stopping beats carrying on.
   */
  readonly stalled: boolean
  /** No steps were run, because the driver is paused. */
  readonly paused: boolean
}

export interface SimulationDriverOptions {
  /**
   * Largest wall-clock interval a single frame may represent, seconds.
   *
   * A tab returning from the background reports a delta of minutes. Simulating it would hang
   * the page for a quarter of an hour and then show a ball that drained fourteen times.
   */
  readonly maxFrameSeconds?: number
  /** Steps per frame before the driver declares lost time. Defaults to `MAX_STEPS_PER_FRAME`. */
  readonly maxStepsPerFrame?: number
  /** How many recent frames the stall detector looks at. */
  readonly stallWindowFrames?: number
  /** Lost steps within that window that count as a stall. */
  readonly stallLostSteps?: number
  /**
   * Frames within the window that must have dropped something for it to count.
   *
   * Two, so a **single** hitch never stops the game. One frame of lost time is a garbage
   * collection or a shader compile: it has already happened, pausing does not give it back,
   * and a machine that stops dead every time the browser sneezes is worse than the sneeze.
   * What is worth stopping for is a machine that cannot keep up, and that shows as loss in
   * frame after frame.
   */
  readonly stallMinFrames?: number
}

const DEFAULT_MAX_FRAME_SECONDS = 0.25
/** Half a second of frames at 60 Hz — long enough to tell a hitch from a machine in trouble. */
const DEFAULT_STALL_WINDOW_FRAMES = 30
/** A quarter-second of simulation: at six metres a second, more than a playfield of ball. */
const DEFAULT_STALL_LOST_STEPS = 250
const DEFAULT_STALL_MIN_FRAMES = 2

export class SimulationDriver {
  /** Unconsumed simulation time, seconds. Always in [0, STEP_DT). */
  private accumulator = 0
  /** Steps completed since construction. Not the world's own index — the driver owns this. */
  private steps = 0
  private readonly maxFrameSeconds: number
  private readonly maxStepsPerFrame: number

  private readonly stallWindow: Int32Array
  private readonly stallLostSteps: number
  private readonly stallMinFrames: number
  private stallCursor = 0
  private stalled = false
  private paused = false

  /** Total steps the driver has had to drop. Non-zero means the machine cannot keep up. */
  lostSteps = 0

  /** Times sustained lost time has been detected. A diagnostic, and a support question. */
  stalls = 0

  /**
   * Frames whose wall-clock delta was longer than one frame may represent, and was discarded.
   *
   * Almost always a backgrounded tab, and discarding is right: simulating four minutes of
   * elapsed time would hang the page and then show a ball that drained fourteen times. It is
   * counted rather than swallowed because "the step count does not match the wall clock" is
   * otherwise an unanswerable question — and because the other explanation for that symptom,
   * a machine that cannot keep up, is a completely different problem with a different fix.
   */
  framesClamped = 0

  constructor(options: SimulationDriverOptions = {}) {
    this.maxFrameSeconds = options.maxFrameSeconds ?? DEFAULT_MAX_FRAME_SECONDS
    this.maxStepsPerFrame = options.maxStepsPerFrame ?? MAX_STEPS_PER_FRAME
    this.stallWindow = new Int32Array(
      Math.max(1, options.stallWindowFrames ?? DEFAULT_STALL_WINDOW_FRAMES),
    )
    this.stallLostSteps = options.stallLostSteps ?? DEFAULT_STALL_LOST_STEPS
    this.stallMinFrames = options.stallMinFrames ?? DEFAULT_STALL_MIN_FRAMES
  }

  /** Whether the driver is currently running no steps at all. */
  get isPaused(): boolean {
    return this.paused
  }

  /** Whether sustained lost time has been detected and not yet acknowledged. */
  get isStalled(): boolean {
    return this.stalled
  }

  /**
   * Stop advancing the simulation.
   *
   * The accumulator is discarded rather than kept. Keeping it would deliver the whole pause as
   * a burst of steps on resume, and the ball would jump to wherever it would have been — which
   * is precisely the stolen ball the pause exists to prevent.
   */
  pause(): void {
    if (this.paused) return
    this.paused = true
    this.accumulator = 0
    this.stallWindow.fill(0)
  }

  /** Start advancing again, and clear the stall report. */
  resume(): void {
    this.paused = false
    this.stalled = false
    this.accumulator = 0
    this.stallWindow.fill(0)
  }

  /**
   * Advance exactly one step, while paused (M9-18, 11 §4).
   *
   * **Only while paused, and that is the whole design.** Single-stepping a running simulation is
   * not a thing: the frame loop is already advancing it, and a step squeezed in beside those would
   * be an extra millisecond of world nobody asked for. So this refuses unless the machine is
   * stopped, which is also the only state in which an author can see the result.
   *
   * The accumulator is left alone. It holds the fraction of a step that had not been consumed when
   * play stopped, and spending it here would make the first step after a freeze shorter than a
   * step — which is exactly the kind of thing somebody stepping frame by frame is looking for.
   */
  stepOnce(step: () => void): boolean {
    if (!this.paused) return false
    step()
    this.steps++
    return true
  }

  /**
   * Forget the stall report, without touching the clock.
   *
   * For the frames where there is nothing to lose — attract, the shooter lane, a bonus count.
   * `resume()` would do it too, but `resume()` also discards the accumulator, and calling that
   * every frame would throw away the sub-step remainder sixty times a second and run the
   * simulation permanently slow.
   *
   * **Clearing rather than ignoring is the point.** `recordForStall` latches: once stalled it
   * stops looking, so a report raised while the ball sat in the lane would still be standing the
   * instant the plunger was pulled, and the machine would pause on the first frame of play for
   * time nobody was playing.
   */
  clearStall(): void {
    this.stalled = false
    this.stallWindow.fill(0)
  }

  get stepCount(): number {
    return this.steps
  }

  /** Fraction of a step currently unconsumed. Exposed for tests and diagnostics. */
  get pending(): number {
    return this.accumulator
  }

  /**
   * Advance the simulation to cover `deltaSeconds` of wall clock, then report the frame.
   *
   * `step` is called once per simulation step and is the *only* thing that may advance the
   * world. It takes no arguments on purpose: a step that could see the frame time could use it.
   */
  advance(deltaSeconds: number, step: () => void): FrameState {
    if (this.paused) {
      // Held, not advanced. `alpha` keeps its last value so the renderer draws the same frozen
      // picture every frame rather than snapping to a step boundary the moment play stops.
      return {
        step: this.steps,
        alpha: this.accumulator / STEP_DT,
        stepsRun: 0,
        stepsDropped: 0,
        stalled: this.stalled,
        paused: true,
      }
    }

    let delta = deltaSeconds
    if (!Number.isFinite(delta) || delta < 0) delta = 0
    if (delta > this.maxFrameSeconds) {
      delta = this.maxFrameSeconds
      this.framesClamped++
    }

    this.accumulator += delta

    let stepsRun = 0
    while (this.accumulator >= STEP_DT) {
      if (stepsRun >= this.maxStepsPerFrame) break
      this.accumulator -= STEP_DT
      step()
      this.steps++
      stepsRun++
    }

    // Whatever is left over after the budget is spent is time the machine could not afford.
    // Declared rather than carried, because carrying it is what turns a slow frame into a
    // death spiral.
    let stepsDropped = 0
    if (this.accumulator >= STEP_DT) {
      stepsDropped = Math.floor(this.accumulator / STEP_DT)
      this.accumulator -= stepsDropped * STEP_DT
      this.lostSteps += stepsDropped
    }

    this.recordForStall(stepsDropped)

    return {
      step: this.steps,
      alpha: this.accumulator / STEP_DT,
      stepsRun,
      stepsDropped,
      stalled: this.stalled,
      paused: false,
    }
  }

  /**
   * Slide this frame into the stall window and decide whether the machine is in trouble.
   *
   * A ring buffer of per-frame losses rather than a running total, because what matters is
   * *recent* loss. A machine that dropped six hundred steps while it compiled its shaders and
   * has been perfect for the minute since is not stalling, and a running total can never say
   * so — it would pause a healthy game, once, for something that finished long ago.
   */
  private recordForStall(stepsDropped: number): void {
    this.stallWindow[this.stallCursor] = stepsDropped
    this.stallCursor = (this.stallCursor + 1) % this.stallWindow.length
    if (this.stalled) return

    let total = 0
    let frames = 0
    for (const dropped of this.stallWindow) {
      if (dropped <= 0) continue
      total += dropped
      frames++
    }
    if (total < this.stallLostSteps || frames < this.stallMinFrames) return

    this.stalled = true
    this.stalls++
  }

  reset(): void {
    this.accumulator = 0
    this.steps = 0
    this.lostSteps = 0
    this.stalls = 0
    this.framesClamped = 0
    this.stalled = false
    this.paused = false
    this.stallWindow.fill(0)
    this.stallCursor = 0
  }
}

/** Linear interpolation for the renderer. Never used by the simulation. */
export function lerp(previous: number, current: number, alpha: number): number {
  return previous + (current - previous) * alpha
}

/**
 * Shortest-arc interpolation for an angle, radians.
 *
 * A flipper crossing ±π between two steps would otherwise be drawn sweeping the long way
 * round — a full rotation in one frame, once a stroke, which reads as a rendering glitch and
 * is actually a wrapping bug.
 */
export function lerpAngle(previous: number, current: number, alpha: number): number {
  let delta = current - previous
  while (delta > Math.PI) delta -= 2 * Math.PI
  while (delta < -Math.PI) delta += 2 * Math.PI
  return previous + delta * alpha
}

export { STEP_DT, STEP_HZ }
